<template>
  <div>
    <div v-if="isEditing" class="mb-2 membership-name">
      <b>{{ membership.membership_program_name }}</b>
      <span v-if="membership.auto_renews" class="hint-label">{{
        renewsText
      }}</span>
    </div>
    <div
      v-else
      class="full-width membership-row mb-1 flex flex-col md:flex-row md:gap-3"
    >
      <SelectInput
        v-model="formData.membership_program_id"
        :options="options"
        placeholder="Select Membership Program"
        label="Membership Program"
        :filter="true"
        :validation="
          firstErrorMessage(v$.formData?.membership_program_id) ? 'error' : ''
        "
        :helper-text="firstErrorMessage(v$.formData?.membership_program_id)"
        class="w-full mt-1"
      >
        <template #option="{ option }">
          <div class="whitespace-normal">
            {{ option.label }}
            <span v-if="option.hintLabel" class="hint-text ml-1"
              >({{ option.hintLabel }})</span
            >
          </div>
        </template>
      </SelectInput>
      <div class="flex items-center gap-3 w-full">
        <NumberInput
          v-model="formData.covered_systems"
          :min="1"
          label="Covered System(s)"
        />
        <sera-input-currency
          v-if="formData.auto_renews"
          :cents="totalPrice"
          label="Charge on Renewal"
          placeholder="Price"
          :errorMessage="firstErrorMessage(v$.formData?.auto_renews)"
          class="mt-1"
          @change="({ detail }) => (totalPrice = Number(detail))"
        />
      </div>
    </div>
    <div class="flex flex-col gap-3 md:flex-row">
      <NumberInput
        v-if="isEditing"
        v-model="formData.covered_systems"
        :min="1"
        label="Covered System(s)"
      />
      <Datepicker
        key="starts_at"
        v-model="formData.starts_at"
        name="Starts On"
        :field-error-message="firstErrorMessage(v$.formData?.starts_at)"
      />
      <Datepicker
        v-if="formData.auto_renews"
        key="renews-input"
        v-model="formData.renews_at"
        name="Next Billing Date"
        :field-error-message="firstErrorMessage(v$.formData?.renews_at)"
      />
      <Datepicker
        v-if="!formData.auto_renews || isEditing"
        key="ends-input"
        v-model="formData.ends_at"
        :name="endTag"
        :hint-label="formData.auto_renews ? '(optional)' : ''"
        :field-error-message="firstErrorMessage(v$.formData?.ends_at)"
      />
    </div>
    <div v-if="addressOptions.length" class="mt-3">
      <SelectInput
        v-model="formData.address_ids"
        :options="addressOptions"
        :multiselect="true"
        :filter="true"
        :body-render="false"
        :max-height="176"
        placeholder="Select Service Address(es)"
        label="Service Address(es)"
        :validation="firstErrorMessage(v$.formData?.address_ids) ? 'error' : ''"
        :helper-text="firstErrorMessage(v$.formData?.address_ids)"
      />
    </div>
    <div v-if="showNonRenewalAlert" class="mt-3">
      <span class="text-danger"><b>This membership will not renew.</b></span>
    </div>
    <div v-if="showRenewalText" class="mt-3">
      <span
        >This membership will continue to bill until the cancellation
        date.</span
      >
    </div>
  </div>
</template>

<script setup>
import moment from "moment";
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { firstErrorMessage } from "@/lib/vuelidate_helpers";

import Datepicker from "@/components/Seranova/Datepicker/CalendarDatepicker.vue";
import NumberInput from "@/components/Seranova/NumberInput/NumberInput.vue";
import SelectInput from "@/components/Seranova/SelectInput/SelectInput.vue";
import { fullAddress } from "@/mixins";
import { displayMembershipDuration } from "@/mixins/membership.js";
import useVuelidate from "@vuelidate/core";
import { helpers, required, requiredIf } from "@vuelidate/validators";

const store = useStore();
const memberships = computed(() => store.state.membershipPrograms);

const formData = ref({});
const selectedMembership = computed(() => {
  if (!formData.value.membership_program_id || !memberships.value) return;
  return memberships.value.find(
    (membership) => membership.id === formData.value.membership_program_id
  );
});

const startDateValidation = helpers.withMessage(
  `Start date should be before end date`,
  (startDate, formData) => {
    const endDate = formData.ends_at;
    if (!startDate || !endDate) return true;
    return moment(endDate).diff(moment(startDate), "days") >= 0;
  }
);

const renewsValidation = helpers.withMessage(
  `Date cannot be in the past`,
  (cancellationDate) => {
    if (!cancellationDate) return true;
    return moment().diff(cancellationDate, "days") <= 0;
  }
);

const endDateValidation = helpers.withMessage(
  `End date should be after start date`,
  (endDate, formData) => {
    const startDate = formData.starts_at;
    if (!startDate || !endDate) return true;
    return moment(endDate).diff(moment(startDate), "days") >= 0;
  }
);

const validationRules = {
  formData: {
    membership_program_id: { required, $autoDirty: true },
    starts_at: {
      required,
      $autoDirty: true,
      startDateValidation
    },
    renews_at: {
      $autoDirty: true,
      requiredCustom: helpers.withMessage(
        () =>
          isEditing.value ? "Unconfirmed" : "The Next Billing Date is required",
        requiredIf(() => formData.value.auto_renews)
      ),
      renewsValidation
    },
    ends_at: {
      $autoDirty: true,
      requiredIf: requiredIf(() => !formData.value.auto_renews),
      endDateValidation
    },
    address_ids: {
      minLength: helpers.withMessage(
        "At least one address is required",
        (value) => {
          if (customerAddresses.value.length === 0) return true;
          return Array.isArray(value) && value.length >= 1;
        }
      )
    }
  },
  totalPrice: { required, $autoDirty: true }
};

const totalPrice = ref(calcTotalPrice());
const validationState = {
  formData,
  totalPrice
};
const v$ = useVuelidate(validationRules, validationState);

const props = defineProps({
  membership: {
    type: Object,
    default: null
  },
  customerId: {
    type: Number,
    default: null
  },
  serviceAddress: {
    type: Object,
    default: null
  }
});

const customerAddresses = computed(
  () => store.state.currentUserAddresses || []
);

const addressOptions = computed(() => {
  return customerAddresses.value.map((addr) => ({
    value: addr.id,
    label: fullAddress(addr)
  }));
});

const options = computed(() => {
  return memberships.value.map((mp) => ({
    value: mp.id,
    label: `${mp.set_as_standard ? "*" : ""}${mp.name}`,
    hintLabel: mp.is_active ? displayMembershipDuration(mp) : "Inactive"
  }));
});

const endTag = computed(() => {
  return props.membership.renews_at ? "Cancellation Date" : "Ends On";
});

const renewsText = computed(() => {
  return `(${displayMembershipDuration(props.membership)})`;
});

const isEditing = computed(() => {
  return props.membership && props.membership.membership_id;
});

const showNonRenewalAlert = computed(() => {
  return (
    (formData.value.membership_program_id && !formData.value.auto_renews) ||
    (formData.value.auto_renews &&
      formData.value.ends_at &&
      renewsAfterEnds.value)
  );
});

const showRenewalText = computed(() => {
  return (
    formData.value.membership_program_id &&
    formData.value.auto_renews &&
    (!formData.value.ends_at || !renewsAfterEnds.value)
  );
});

const renewsAfterEnds = computed(() => {
  return moment(formData.value.renews_at).isAfter(
    moment(formData.value.ends_at),
    "day"
  );
});

watch(
  () => formData.value.membership_program_id,
  (newValue, oldValue) => {
    if (newValue !== oldValue) handleMembershipChange();
  }
);

watch(
  () => formData.value.covered_systems,
  (newValue, oldValue) => {
    if (newValue !== oldValue) handleCoveredSystemsChange();
  }
);

watch(selectedMembership, (newValue, oldValue) => {
  if (newValue !== oldValue && !oldValue) totalPrice.value = calcTotalPrice();
});

onMounted(async () => {
  if (props.membership) {
    formData.value = {
      ...props.membership,
      covered_systems: props.membership.covered_systems || 1,
      address_ids: []
    };
    totalPrice.value = calcTotalPrice();
  }
  if (props.customerId) {
    await store.dispatch("fetchCustomerAddresses", {
      customerId: props.customerId
    });
  }
  // Only seed the picker once we can confirm the invoice address is one of
  // the customer's addresses; otherwise the controller silently drops it on
  // save and the UI would diverge from what gets persisted.
  if (
    props.serviceAddress &&
    customerAddresses.value.some((a) => a.id === props.serviceAddress.id)
  ) {
    formData.value.address_ids = [props.serviceAddress.id];
  }
});

function getFormData() {
  return new Promise((resolve, reject) => {
    v$.value.$validate().then((isValid) => {
      if (isValid) {
        resolve({
          ...formData.value,
          price_cents: Number(totalPrice.value),
          address_ids: formData.value.address_ids || []
        });
      } else {
        reject(new Error("Form validation failed!"));
      }
    });
  });
}

function getEndDate(membership) {
  return moment()
    .add(membership.duration.count, membership.duration.period)
    .toDate();
}

function calcTotalPrice(coveredSystems = null) {
  if (!selectedMembership.value) return 0;
  coveredSystems = coveredSystems || formData.value.covered_systems || 1;
  const additionalSystems = coveredSystems - 1;
  const additionalSystemPrice =
    selectedMembership.value.additional_unit_price_cents || 0;

  const totalPrice =
    selectedMembership.value.price_cents +
    additionalSystemPrice * additionalSystems;
  return totalPrice;
}

function handleMembershipChange() {
  totalPrice.value = calcTotalPrice();
  if (!selectedMembership.value) return;

  let { ends_at, renews_at, starts_at } = props.membership;

  if (!isEditing.value) {
    const endDate = getEndDate(selectedMembership.value);
    ends_at = selectedMembership.value.auto_renews ? null : endDate;
    renews_at = selectedMembership.value.auto_renews ? endDate : null;
    starts_at = new Date();
  }

  formData.value = {
    ...formData.value,
    auto_renews: selectedMembership.value.auto_renews,
    starts_at,
    renews_at,
    ends_at
  };
}

function handleCoveredSystemsChange() {
  totalPrice.value = calcTotalPrice();
}

defineExpose({
  getFormData
});
</script>

<style lang="scss" scoped>
.membership-row {
  display: flex;
  align-items: baseline;
}

.membership-name {
  font-size: 1rem;
}
</style>
